/* eslint-disable react/jsx-no-target-blank */
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { Flex, Text, Heading, Spinner, useToast } from "@chakra-ui/react";
import { Decimal } from "@syla/shared/decimal";
import { getLastFinancialYear } from "@syla/shared/helpers/financialYear";
import {
  satisfiesEntityType,
  satisfiesActivityType,
  satisfiesTransactionCount,
  satisfiesAllRequirements,
} from "@syla/shared/types/helpers/permissions";
import { getSubscriptionState } from "@syla/shared/types/helpers/subscriptions";
import {
  AccountType,
  AccountSubType,
  ActivityTypeName,
} from "@syla/shared/types/models/AccountBase";
import React, { useState, useCallback } from "react";
import { isBrowser } from "react-device-detect";
import { useLocation } from "react-router-dom";
import { PageContainer } from "../components/atoms/Containers";
import { errorToastOptions } from "../components/molecules/errorToastOptions";
import { PlanCard, Feature } from "../components/molecules/PlanCard";
import { getFullPageUrl } from "../helper/getFullPageUrl";
import {
  canPurchaseSubscription,
  canManageExistingSubscription,
} from "../helper/permissions";
import { useCurrentAccountStore } from "../store/currentAccountStore";
import { Plan } from "../types/plans/plan";
import { Subscription } from "../types/plans/subscription";
import { stripePurchaseOrUpgrade } from "./stripePurchaseOrUpgrade";
import { useGetAccountPlanProps } from "./useGetAccountPlanProps";

export type PlansNavState = {
  referringUrl: string;
  selectedFinancialYear?: string;
};

function getUnsuitablePlanMessage({
  plan,
  account,
  transactionCount,
}: {
  account: { accountType: AccountType; accountSubType: AccountSubType };
  plan: Plan;
  transactionCount: number;
}) {
  if (!satisfiesEntityType({ account, plan })) {
    const article = account.accountType === AccountType.SMSF ? "an" : "a";
    return `This plan doesn't support ${article} ${account.accountType} account. Update your Account Type in Settings or choose a compatible plan.`;
  }

  if (!satisfiesActivityType({ account, plan })) {
    return `This plan doesn't support ${ActivityTypeName[AccountSubType.Trader]} activity. Update your Activity Type in Settings or choose a compatible plan.`;
  }

  if (!satisfiesTransactionCount({ plan, transactionCount })) {
    return "The number of transactions in your account exceeds the limit on this plan.";
  }

  throw new Error(`Invalid case`);
}

export const Plans = () => {
  const { state } = useLocation<PlansNavState | undefined>();
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);
  const errorToast = useToast(errorToastOptions);

  const selectedFinancialYear =
    state?.selectedFinancialYear ?? getLastFinancialYear();

  const {
    availablePlans,
    currentSubscription,
    currentPlan,
    recommendedPlan,
    transactionCount,
    isLoading: isAccountPlansLoading,
    account,
  } = useGetAccountPlanProps();

  const hasPurchasePermission =
    canPurchaseSubscription({
      account,
      subscriptions: currentSubscription ? [currentSubscription] : [],
    }) ||
    canManageExistingSubscription({
      subscriptions: currentSubscription ? [currentSubscription] : [],
    });

  const isValidSubscription = (sub) => {
    const { valid, lapsed } = getSubscriptionState(sub);
    return valid && !lapsed;
  };
  const actionNeeded =
    currentSubscription && !isValidSubscription(currentSubscription);

  const openCheckoutSession = useCallback(
    async (plan: Pick<Plan, "_id">, subscription?: Subscription) => {
      try {
        setRedirecting(true);
        await stripePurchaseOrUpgrade({
          accountId,
          successUrl: state?.referringUrl ?? getFullPageUrl(location),
          cancelUrl: getFullPageUrl(location),
          financialYear: selectedFinancialYear,
          stripeSubscriptionId: subscription?.stripeSubscriptionId,
          plan,
          currentPlan: currentSubscription?.plan,
        });
      } catch (error) {
        errorToast({
          title: "Error selecting plan",
          description: `An unexpected error occurred. Please try again later.`,
        });
        return;
      } finally {
        setRedirecting(false);
      }
    },
    [
      accountId,
      currentSubscription?.plan,
      errorToast,
      selectedFinancialYear,
      state?.referringUrl,
    ],
  );

  if (isAccountPlansLoading) {
    return (
      <PageContainer isBrowser={isBrowser}>
        <Flex justifyContent="center">
          <Spinner
            color="red.500"
            emptyColor="red.200"
            my="50px"
            thickness="5px"
            size="xl"
          />
        </Flex>
      </PageContainer>
    );
  }

  return (
    <PageContainer isBrowser={isBrowser}>
      <Heading
        as="h1"
        size="2xl"
        color="gray.800"
        textAlign="center"
        fontFamily="GintoNormal"
        mb={8}
      >
        Tax Plans
      </Heading>

      <Flex justify="center" w="full">
        <TransactionCountDisplay transactionCount={transactionCount} />
      </Flex>

      <Flex w="full" justifyContent="center" flexWrap="wrap" mt="20px">
        {availablePlans.map((plan) => {
          const isCurrentPlan = currentPlan?._id === plan._id;

          let expiredDate: string | undefined = undefined;
          let cancelDate: string | undefined = undefined;
          let purchased = false;
          let invalid = false;

          if (isCurrentPlan && currentSubscription) {
            const { lapsed, cancelled, lapsesOn, valid } =
              getSubscriptionState(currentSubscription);
            const endDateFormatted = lapsesOn.toLocaleDateString();
            if (lapsed) expiredDate = endDateFormatted;
            if (cancelled) cancelDate = endDateFormatted;
            if (valid) purchased = true;
            else invalid = true;
          }

          const recommended = plan._id === recommendedPlan?._id;

          const isPlanSuitable = satisfiesAllRequirements({
            account,
            plan,
            transactionCount,
          });

          return (
            <PlanCard
              key={plan._id}
              price={plan.price}
              heading={plan.name}
              description={plan.description}
              featureList={getFeatureList(plan)}
              action={() => openCheckoutSession(plan, currentSubscription)}
              purchasable={
                hasPurchasePermission &&
                ((actionNeeded && isCurrentPlan) ||
                  (!actionNeeded && isPlanSuitable))
              }
              canManage={hasPurchasePermission}
              notPurchasableReason={getNotPurchasableReason(
                hasPurchasePermission,
                isPlanSuitable,
                account,
                transactionCount,
                plan,
              )}
              headerText={
                expiredDate ? `Expired ${expiredDate}`
                : cancelDate ?
                  `Expires ${cancelDate}`
                : invalid ?
                  "Action Required"
                : purchased ?
                  "Subscribed"
                : recommended && !actionNeeded ?
                  "Recommended"
                : undefined
              }
              status={
                expiredDate ? "expired"
                : cancelDate ?
                  "cancelled"
                : invalid ?
                  "actionRequired"
                : purchased ?
                  "purchased"
                : recommended && !actionNeeded ?
                  "recommended"
                : "normal"
              }
              purchased={isCurrentPlan}
              purchasedPrice={currentPlan?.price}
              upgrading={actionNeeded}
              previousPrice={0}
              isBusy={redirecting}
            />
          );
        })}
      </Flex>
    </PageContainer>
  );
};

const TransactionCountDisplay = ({
  transactionCount,
}: {
  transactionCount: number | undefined;
}) => {
  const getTransactionDisplay = () => {
    if (transactionCount === undefined) {
      return <Spinner size="xs" />;
    }
    return Decimal.from(transactionCount.toString()).format({
      separateThousands: true,
    }).text;
  };

  return (
    <Flex align="center" gap={2}>
      <InfoOutlineIcon />
      <Text fontSize="sm" color="gray.600" fontWeight="medium">
        You currently have{" "}
        <strong>{getTransactionDisplay()} transactions</strong> on your account.
      </Text>
    </Flex>
  );
};

function getFeatureList(plan: Plan): Feature[] {
  const transactionCap = Decimal.from(
    plan.features.transactionLimit.toString(),
  ).format({
    separateThousands: true,
  }).text;

  return [
    {
      text: `${transactionCap} Transactions`,
      included: true,
    },
    {
      text: "All Financial Years",
      included: true,
    },
    {
      text: "Crypto Tax Report",
      included: true,
    },
    {
      text: "SMSF, Company and Trust",
      included: plan.features.entities,
    },
    {
      text: "BGL Sync",
      included: plan.features.premiumIntegrations,
    },
    {
      text: "Assurance Reports",
      included: plan.features.assuranceReports,
    },
    {
      text: "LTFO Tax Saving",
      included: plan.features.taxOptimisation,
    },
    {
      text: "Trading Stock",
      included: plan.features.tradingStock,
    },
  ];
}

function getNotPurchasableReason(
  canPurchase: boolean,
  isPlanSuitable: boolean,
  account,
  transactionCount,
  plan,
) {
  if (!canPurchase) {
    return "Please contact the billing owner for this account to purchase a plan";
  }

  if (!isPlanSuitable && !!account && transactionCount !== undefined) {
    return getUnsuitablePlanMessage({
      account,
      plan,
      transactionCount,
    });
  }

  if (transactionCount === undefined) {
    return "Please wait for your tax calculations to complete";
  }
}
